import { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useSelector, useStore } from 'statux'

import AdvancedMembershipCheckout, {
    ADVANCED_MEMBERSHIP_CHECKOUT_STEPS
} from '../../../components/AdvancedMembershipCheckout/AdvancedMembershipCheckout'
import ArticleCard from '../../../components/ArticleCard/ArticleCard'
import Details from '../../../components/Details/Details'
import EventCard from '../../../components/EventCard/EventCard'
import FounderTracRegistration from '../../../components/FounderTracRegistration/FounderTracRegistration'
import Image from '../../../components/Image/Image'
import Swiper from '../../../components/Swiper/Swiper'

import DashboardHomeNetwork from './DashboardHomeNetwork/DashboardHomeNetwork'
import DashboardHomeEbpcJudge from './DashboardHomeEbpcJudge/DashboardHomeEbpcJudge'
import DashboardHomeEbpcReviewer from './DashboardHomeEbpcReviewer/DashboardHomeEbpcReviewer'
import DashboardHomeEbpcAside from './DashboardHomeEbpcAside/DashboardHomeEbpcAside'

import { graphqlClient, gql } from '../../../utils/gql'
import { getCssVar } from '../../../utils/css'
import { getQueryParams } from '../../../utils/location'
import authProvider from '../../../utils/auth-provider'

export default function DashboardHome (props = {
    greetingsPrefix: null
}) {
    const userId = (authProvider.getIdentity() || {}).id

    const isAdmin = authProvider.getDefaultRole() === 'admin'

    const isProfileApproved = !authProvider.getDefaultRole()
        .match(/^(admin|user)$/)

    const isMember = authProvider.getDefaultRole() === 'member'

    const isAdvisor = authProvider.getDefaultRole() === 'advisor'

    const isEbpcJudge = authProvider.getDefaultRole() === 'ebpc_judge'

    const isEbpcReviewer = authProvider.getDefaultRole() === 'ebpc_reviewer'

    const history = useHistory()

    const location = useLocation()

    const [articles, setArticles] = useState(
        (history.location.state || {}).articles
            || null)

    const [events, setEvents] = useState(
        (history.location.state || {}).events
            || null)

    const [profile, setProfile] = useState(
        ((history.location.state || {}).profile || {}).email
                && ((history.location.state || {}).profile || {}).email
                    === (authProvider.getIdentity() || {}).email
            ? history.location.state.profile
            : null)

    const [modal, setModal] = useStore('modal')

    const now = new Date()

    const today = new Date(now.setHours(0))

    const foundertracAccessToken = useSelector('foundertracAccessToken')

    const {
        ebpcContest,
        ebpcApplicationsOpen,
        ebpcApplicationsEndDateStr,
        advancedMembershipAt
    } = useSelector()

    useEffect(async () => {
        if (!userId)
            return

        const profileQuery = isMember || isAdvisor
            ? gql`
                profiles (
                    limit: 1,
                    where: {
                        user: { id: { _eq: "${userId}" }}
                    }
                ) {
                    id
                    email
                    role
                    first_name
                    last_name
                    bio
                    title
                    company
                    company_logo
                    company_bio
                    company_website
                    company_goals
                    advanced_membership
                    advanced_membership_application
                    user {
                        id
                        avatar_url
                    }
                    foundertrac_consented_at
                    foundertrac_account {
                        access_token
                    }
                    ebpc_applications {
                        id
                        created_at
                        submitted_at
                        ebpc_contest_id
                    }
                }`
            : ''

        const membersFilter = 'articlesRecommendedMembersFilter'

        const advisorsFilter = 'articlesRecommendedAdvisorsFilter'

        const articlesWhereArgs = [
            gql`{ recommended_roles: { _contains: $${isMember
                ? membersFilter
                : advisorsFilter} } }`,
            isAdmin
                ? gql`{ recommended_roles: { _contains: $${membersFilter} } }`
                : null
        ].filter(str => str)

        const query = await graphqlClient
            .request(gql`
                query (
                    $${membersFilter}: jsonb,
                    $${advisorsFilter}: jsonb
                ) {
                    articles (
                        where: {
                            _or: [
                                ${articlesWhereArgs.join(',')},
                                { source_id: { _is_null: true } }
                            ]
                        },
                        limit: 3,
                        order_by: [
                            { recommended_roles: asc },
                            { created_at: desc }
                        ]
                    ) {
                        id
                        title
                        slug
                        created_at
                        durations
                        thumbnail
                        tags {
                            id
                            slug
                            title
                        }
                        source {
                            id
                            slug
                            title
                        }
                        recommended_roles
                    }

                    events (
                        limit: 9,
                        order_by: { datetime_start: asc }
                        where: {
                            datetime_start: {
                                _gte: "${today.toISOString()}"
                            }
                        },
                    ) {
                        id
                        title
                        text
                        url
                        datetime_start
                        datetime_end
                    }

                    ${profileQuery}
                }
            `, {
                [`${membersFilter}`]: {
                    members: true
                },
                [`${advisorsFilter}`]: {
                    advisors: true
                }
            })
            .catch(err => {
                console.warn(err)
                return {}
            })

        const updateArticles = ((query.articles || []).length
            ? query.articles
            : articles
        ) || false

        if (JSON.stringify(articles)
                !== JSON.stringify(updateArticles))
            setArticles(updateArticles)

        const updateEvents = ((query.events || []).length
            ? query.events
            : events
        ) || false

        if (JSON.stringify(events)
                !== JSON.stringify(updateEvents))
            setEvents(updateEvents)

        const queryProfile = (query.profiles || [])[0]
            || false

        if (JSON.stringify(profile)
                !== JSON.stringify(queryProfile))
            setProfile(queryProfile)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                articles: updateArticles,
                events: updateEvents,
                profile: queryProfile
            }))
    }, [userId])

    const isProfileComplete = isEbpcJudge
        || isEbpcReviewer
        || !isProfileApproved
        || Boolean(profile
            && profile.first_name
            && profile.last_name
            && profile.bio
            && profile.title
            && profile.company
            && profile.company_logo
            && profile.company_website
            && (profile.user || {}).avatar_url
            && (!isMember
                || profile.company_goals))

    let { advancedMembersApplyEndDate } = useSelector('options')

    if (advancedMembersApplyEndDate)
        advancedMembersApplyEndDate = new Date(advancedMembersApplyEndDate)

    const isAdvancedMember = Boolean(isMember
        && (advancedMembershipAt
            || (profile
                && profile.advanced_membership
                && (profile.advanced_membership_application || {})
                    .complete)))

    const isAdvancedMembersAlert = isMember
        && !isAdvancedMember
        && (!advancedMembersApplyEndDate
            || advancedMembersApplyEndDate.getTime()
                > (new Date()).getTime())

    const isFounderTracCreateAlert = (isAdvancedMember
            || isAdvisor)
        && Boolean(profile
            && !profile.foundertrac_consented_at
            && !foundertracAccessToken)

    const isFounderTracManageAlert = (isAdvancedMember
            || isAdvisor)
        && Boolean(!isFounderTracCreateAlert
            && foundertracAccessToken)

    const isEbpcApplicantAlert = profile
        && profile.role === 'member'
        && ebpcApplicationsOpen
        && !(profile.ebpc_applications || [])
            .filter(obj => obj.ebpc_contest_id === (ebpcContest || {}).id)
            .length

    const isAlerts = !isAdmin
        && profile !== null
        && (!isProfileComplete
            || !isProfileApproved
            || isAdvancedMembersAlert
            || isFounderTracCreateAlert
            || isFounderTracManageAlert
            || isEbpcApplicantAlert)

    useEffect(() => {
        if (!profile)
            return

        const modalClassName = 'modal-advanced-membership-checkout'

        const getStep = () => getQueryParams()['advanced-membership-checkout']

        const step = getStep()

        if (!isMember
                || profile.advanced_membership
                || (modal
                    && modal.className !== modalClassName))
            return step
                && history.replace(`${history.location.pathname}`)

        if (!step
                || (step !== 'status'
                    && advancedMembershipAt)) {
            if (step)
                history.replace(`${history.location.pathname}`)

            return modal
                && modal.className === modalClassName
                && setModal(false)
        }

        const isFirstStep = ADVANCED_MEMBERSHIP_CHECKOUT_STEPS.indexOf(step)
            === 0

        const paymentStepTitle
            = 'Accelerate your venture\'s mission with Advanced Membership'

        const renderTitlebarCta = () => !isFirstStep
            && (
                <button
                    className="btn-unstyled"
                    style={{
                        fontWeight: 300,
                        fontSize: 14,
                        color: 'var(--slate)'
                    }}
                    onClick={() => {
                        const prevStepIdx = ADVANCED_MEMBERSHIP_CHECKOUT_STEPS
                            .indexOf(getStep()) - 1

                        const prevStep = ADVANCED_MEMBERSHIP_CHECKOUT_STEPS[prevStepIdx]

                        history.push(`${history.location.pathname
                            }?advanced-membership-checkout=${prevStep}`)
                    }}
                    aria-label="Go back"
                >
                    <svg
                        className="icon-chevron-left"
                        width="18"
                        height="18"
                    >
                        <use xlinkHref="#icon-chevron-left" />
                    </svg>
                    <span className="display-none-lg-down margin-0-l">Back</span>
                </button>
            )

        if (modal) {
            const modalProps = { ...modal }

            if (modal.titlebarCta
                    && step === 'onboard')
                modalProps.titlebarCta = null

            if (!modal.titlebarCta
                    && step !== 'onboard')
                modalProps.titlebarCta = renderTitlebarCta()

            if (modal.title
                    && step !== 'payment')
                modalProps.title = null

            if (!modal.title
                    && step === 'payment')
                modalProps.title = paymentStepTitle

            return setModal(modalProps)
        }

        if (!modal)
            setModal({
                'aria-labelledby': 'modal-heading',
                className: modalClassName,
                dialogScrollable: true,
                size: 'lg',
                component: (
                    <AdvancedMembershipCheckout
                        profile={profile}
                        step={step}
                        onStep={(modalStep) => {
                            const locationStep = getStep()

                            console.groupCollapsed('AdvancedMembershipCheckout.onStep')
                            console.log('Modal step', modalStep)
                            console.log('Location step', locationStep)
                            console.log(history.location)
                            console.groupEnd()

                            if (modalStep === locationStep)
                                return

                            history.push(`${history.location.pathname
                                }?advanced-membership-checkout=${modalStep}`)
                        }}
                    />
                ),
                onClose: () => history.push(history.location.pathname),
                titlebarCta: renderTitlebarCta(),
                title: step === 'payment'
                    && paymentStepTitle
            })
    }, [profile, location.search])

    useEffect(() => {
        if (!profile
                && profile !== null
                && (isMember || isAdvisor))
            console.warn('Profile fetch error:',
                authProvider.getIdentity())
    }, [profile])

    if (articles === null
            || events === null
            || profile === null)
        return null

    return (
        <div className="dashboard-home pad-3-t pad-4-b pad-4-t-lg-up">
            <h1
                className="heading-2 container margin-3-b"
            >
                <span className="row-condensed">
                    <span className="col-12 col-8-xl-up">
                        {(isEbpcJudge || isEbpcReviewer)
                            ? `Welcome to your ${
                                isEbpcJudge
                                    ? 'judging'
                                    : 'reviewing'} portal${
                                authProvider.getDisplayName()
                                        !== (authProvider.getIdentity() || {}).email
                                    ? `, ${authProvider.getDisplayName()}`
                                    : ''}`
                            : 'Dashboard'}
                    </span>
                </span>
            </h1>
            <div className="container">
                <div className="row-condensed">
                    <div
                        className={`${isAdmin
                            ? 'col'
                            : 'col-12 col-8-xl-up'}`}
                    >
                        {isEbpcJudge
                            && <DashboardHomeEbpcJudge />}
                        {isEbpcReviewer
                            && <DashboardHomeEbpcReviewer />}
                        {Boolean((articles || []).length)
                            && !isEbpcJudge
                            && !isEbpcReviewer
                            && (
                                <section className="dashboard-home-articles card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                                    <div className="row-condensed margin-3-b">
                                        <div className="col col-12-md-down margin-b-md-down">
                                            <h2 className="heading-5">
                                                Recommended by the Catapult team.
                                            </h2>
                                            <p
                                                className="body-4"
                                                style={{
                                                    color: getCssVar('--slate')
                                                }}
                                            >
                                                A few pieces to get you started.
                                            </p>
                                        </div>
                                        <div className="col-auto">
                                            <Link
                                                to="/dashboard/hub"
                                                className="btn-outline-secondary-alt btn-sm"
                                            >
                                                <svg className="icon-workspaces">
                                                    <use xlinkHref="#icon-workspaces" />
                                                </svg>
                                                View more content
                                            </Link>
                                        </div>
                                    </div>
                                    <Swiper
                                        breakpoints={{
                                            1024: {
                                                slidesPerView: 3
                                            }
                                        }}
                                        navigation={false}
                                        pagination={false}
                                        slideClassName="col-10 col-7-md col-4-lg-up"
                                        wrapperClassName="row-condensed"
                                    >
                                        {articles
                                            .slice(0, 3)
                                            .map((obj, idx) => (
                                                <ArticleCard
                                                    key={`dashboard-home-articles-${idx}}`}
                                                    idPrefix={`dashboard-home-articles-${idx}`}
                                                    {...obj}
                                                />
                                            ))}
                                    </Swiper>
                                </section>)}
                        {isAlerts
                            && (
                                <Details
                                    className="dashboard-home-notifications card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up pad-0-b"
                                    data-summary-toggle="false"
                                    open
                                >
                                    <summary className="pad-0">
                                        <div className="row-condensed flex-nowrap">
                                            <div className="col">
                                                <h2 className="heading-5 display-flex flex-nowrap flex-align-items-center">
                                                    <span
                                                        className="ratio-square round flex-justify-content-center flex-align-items-center flex-order-last"
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            background: getCssVar('--aqua'),
                                                            fontSize: 12,
                                                            fontWeight: 400,
                                                            marginLeft: 7,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {Array.from([
                                                            isFounderTracCreateAlert,
                                                            isFounderTracManageAlert,
                                                            !isProfileApproved,
                                                            !isProfileComplete,
                                                            isAdvancedMembersAlert,
                                                            isEbpcApplicantAlert
                                                        ]).filter(bool => bool)
                                                            .length}
                                                    </span>
                                                    <span className="flex-order-first">Notifications</span>
                                                </h2>
                                                <p
                                                    className="body-4"
                                                    style={{
                                                        color: getCssVar('--slate')
                                                    }}
                                                >
                                                    Check back for alerts about your account, pairings, and general updates.
                                                </p>
                                            </div>
                                            <div className="col-auto">
                                                <span
                                                    className="body-6"
                                                    aria-hidden="true"
                                                    data-summary-open="Hide"
                                                    data-summary-close="Show"
                                                    style={{ color: getCssVar('--teal') }}
                                                >
                                                    <svg
                                                        className="icon-chevron-right"
                                                        width="16"
                                                        height="16"
                                                    >
                                                        <use xlinkHref="#icon-chevron-right" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </summary>
                                    <hr />
                                    <ul className="pad-0-l pad-0-r margin-0-b">
                                        {isFounderTracCreateAlert
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-membership.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    Create your FounderTrac account
                                                                </h3>
                                                                <p className="body-6 font-weight-book">
                                                                    We’re excited to announce our partnership with FounderTrac
                                                                    to bring needs assessment, milestone mapping, and advisor
                                                                    matching to Catapult members.
                                                                </p>
                                                            </div>
                                                            <div className="col-auto">
                                                                <button
                                                                    className="btn-outline-secondary-alt btn-sm"
                                                                    onClick={() => setModal({
                                                                        'aria-labelledby': 'modal-heading',
                                                                        className: 'modal-foundertrac-registration',
                                                                        size: 'lg',
                                                                        dialogScrollable: true,
                                                                        icon: 'icon-like',
                                                                        iconBackground: getCssVar('--aqua'),
                                                                        component: <FounderTracRegistration profile={profile} />
                                                                    })}
                                                                >
                                                                    <svg
                                                                        className="icon-person"
                                                                        width="16"
                                                                        height="16"
                                                                    >
                                                                        <use xlinkHref="#icon-person" />
                                                                    </svg>
                                                                    Create account
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}
                                        {isFounderTracManageAlert
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-membership.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    Manage your FounderTrac account
                                                                </h3>
                                                                <div className="body-6 font-weight-book">
                                                                    {isMember
                                                                        && (
                                                                            <p>
                                                                                Take the first step toward building a visual map highlighting
                                                                                your venture’s unique areas of focus by managing your FounderTrac account.
                                                                            </p>)}
                                                                    {!isMember
                                                                        && (
                                                                            <p>
                                                                                Take the first step toward toward matching with Catapult ventures
                                                                                by managing your FounderTrac account.
                                                                            </p>)}
                                                                </div>
                                                            </div>
                                                            <div className="col-auto">
                                                                <a
                                                                    href={`${window.FOUNDERTRAC_URL}/authorize/${
                                                                        window.FOUNDERTRAC_CLIENT_ID}?a=${
                                                                        foundertracAccessToken}`}
                                                                    className="btn-outline-secondary-alt btn-sm"
                                                                    rel="noopener noreferrer"
                                                                    target="_blank"
                                                                    title="Opens in a new tab"
                                                                >
                                                                    <svg
                                                                        className="icon-person"
                                                                        width="16"
                                                                        height="16"
                                                                    >
                                                                        <use xlinkHref="#icon-person" />
                                                                    </svg>
                                                                    Manage account
                                                                    <svg
                                                                        className="icon-launch"
                                                                        width="16"
                                                                        height="16"
                                                                        style={{ marginLeft: 5 }}
                                                                    >
                                                                        <use xlinkHref="#icon-launch" />
                                                                    </svg>
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}
                                        {!isProfileApproved
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-profile.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    Your registration is under review
                                                                </h3>
                                                                <p className="body-6 font-weight-book">
                                                                    In the meantime, please explore the Catapult Content Hub,
                                                                    featuring articles, videos, and podcasts from Catapult, PennGSE,
                                                                    and our over 20 partners.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}
                                        {isEbpcApplicantAlert
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-membership.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    Apply to the Milken-Penn GSE Education Business Plan Competition
                                                                </h3>
                                                                <p className="body-6 font-weight-book">
                                                                    Join ventures from around the world who are addressing seminal
                                                                    issues in worldwide education – from early childhood learning to
                                                                    AI-powered tutoring, and everything in between – and compete to win
                                                                    tens of thousands of dollars in cash and prizes.
                                                                    {ebpcApplicationsEndDateStr
                                                                        && ` Application deadline: ${ebpcApplicationsEndDateStr}.`}
                                                                </p>
                                                            </div>
                                                            <div className="col-auto">
                                                                <Link
                                                                    to="/dashboard/ebpc/application"
                                                                    className="btn-outline-secondary-alt btn-sm"
                                                                >
                                                                    Apply
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}
                                        {!isProfileComplete
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-profile.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    Complete your profile
                                                                </h3>
                                                                <p className="body-6 font-weight-book">
                                                                    Set up your profile to personalize your account,
                                                                    share information about your company, and
                                                                    get matched with the right resources.
                                                                </p>
                                                            </div>
                                                            <div className="col-auto">
                                                                <Link
                                                                    to="/dashboard/settings"
                                                                    className="btn-outline-secondary-alt btn-sm"
                                                                >
                                                                    <svg
                                                                        className="icon-person"
                                                                        width="16"
                                                                        height="16"
                                                                    >
                                                                        <use xlinkHref="#icon-person" />
                                                                    </svg>
                                                                    Set up my profile
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}
                                        {isAdvancedMembersAlert
                                            && (
                                                <li
                                                    className="row-condensed flex-align-items-start pad-3-b"
                                                    role="status"
                                                >
                                                    <div className="col-auto">
                                                        <Image
                                                            src="/images/notifications-membership.svg"
                                                            alt="Notifications"
                                                            data-object-fit="contain"
                                                        />
                                                    </div>
                                                    <div className="col">
                                                        <div className="row-condensed">
                                                            <div className="col col-12-xl-down margin-1-b-xl-down pad-3-r-xl-up">
                                                                <h3
                                                                    className="margin-1-b body-4 font-weight-medium"
                                                                    style={{ color: getCssVar('--navy') }}
                                                                >
                                                                    {profile.advanced_membership_application
                                                                        ? 'Finish your application for'
                                                                        : 'Discover the benefits of'} advanced membership.
                                                                </h3>
                                                                <p className="body-6 font-weight-book">
                                                                    Catapult advanced membership is free, and provides you
                                                                    with access to expert advisors and like-minded
                                                                    entrepreneurs. Advanced members are also considered to
                                                                    join the Catapult Cohort and receive premium support in
                                                                    addition to validation grants worth more than $25,000 in
                                                                    cash and in-kind services.
                                                                </p>
                                                            </div>
                                                            <div className="col-auto">
                                                                <Link
                                                                    to="/dashboard/settings/advanced-membership"
                                                                    className="btn-outline-secondary-alt btn-sm"
                                                                >
                                                                    <svg
                                                                        className="icon-person"
                                                                        width="16"
                                                                        height="16"
                                                                    >
                                                                        <use xlinkHref="#icon-person" />
                                                                    </svg>
                                                                    {profile.advanced_membership_application
                                                                        ? 'Continue my application'
                                                                        : 'Learn more and apply today'}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>)}

                                    </ul>
                                </Details>)}
                        {Boolean((events || []).length)
                            && !isEbpcJudge
                            && !isEbpcReviewer
                            && (
                                <section className="dashboard-home-events card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                                    <div className="row-condensed margin-3-b">
                                        <div className="col">
                                            <h2 className="heading-5">
                                                Upcoming events
                                            </h2>
                                        </div>
                                    </div>
                                    <Swiper
                                        wrapperClassName="row-condensed"
                                    >
                                        {events
                                            .filter(obj => obj.url)
                                            .map((obj, idx) => (
                                                <EventCard
                                                    key={`dashboard-home-events-${idx}`}
                                                    idPrefix={`dashboard-home-events-${idx}`}
                                                    {...obj}
                                                />
                                            ))}
                                    </Swiper>
                                </section>)}
                    </div>
                    {(isEbpcJudge || isEbpcReviewer)
                        && <DashboardHomeEbpcAside className="col" />}
                    {(isMember || isAdvisor)
                        && (
                            <aside className="col">
                                <DashboardHomeNetwork profile={profile} />
                            </aside>)}
                </div>
            </div>
        </div>
    )
}
